import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, message, Select } from "antd";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { FormPage, FormCard, inputStyle, grid2, T, cardStyle } from "../../../shared/ui/PageShell";

const readSessionUserName = () => {
  try {
    const raw = localStorage.getItem("sessionUser");
    const u = raw ? JSON.parse(raw) : null;
    return (u?.name || "").trim();
  } catch {
    return "";
  }
};

const Payment = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [amount_paid, setAmount_paid] = useState("");
  const [amount_main, setAmount_main] = useState("");
  const [amount_secondary, setAmount_secondary] = useState("");
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [personName, setPersonName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);
  const mainCode = systemConfig?.currency_code || "IQD";
  const secCode = systemConfig?.secondary_currency_code || "";

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amount_main, 0);
    const sec = toApiNumber(amount_secondary, 0);
    if (!useDualCurrency) return toApiNumber(amount_paid, 0);
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amount_main, amount_secondary, amount_paid, selectedRate, useDualCurrency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (!cfg?.secondary_currency_code) {
          setExchangeRates([]);
          setExchangeRateId(null);
          return;
        }
        const rates = await fetchExchangeRates();
        if (cancelled) return;
        setExchangeRates(rates);
        setExchangeRateId((prev) =>
          prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
        );
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch { /* ignore */ }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/customers/${id}`);
        if (cancelled) return;
        setCustomerInfo({
          name: res?.data?.name || "",
          total_owed: toApiNumber(res?.data?.total_owed, 0),
        });
      } catch {
        if (!cancelled) setCustomerInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const PostApi = async () => {
    if (isSubmitting) return;
    const paidAmt = appliedMainTotal;
    if (!(paidAmt > 0)) {
      message.error(t("vendorsPaymentInvalid", "Enter a valid amount"));
      return;
    }
    const secVal = Math.round(toApiNumber(amount_secondary, 0) * 100) / 100;
    if (useDualCurrency && secVal > 0 && !exchangeRateId) {
      message.error(t("exchangeRateRequired", "Please select an exchange rate"));
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("id", id);
      if (useDualCurrency) {
        formData.append("amount_main", String(Math.round(toApiNumber(amount_main, 0) * 100) / 100));
        formData.append("amount_secondary", String(secVal));
        if (secVal > 0 && exchangeRateId) {
          formData.append("exchange_rate_id", String(exchangeRateId));
        }
      } else {
        formData.append("amount", amount_paid);
      }
      // Record who is logged in — no separate accountant picker.
      const receiver = readSessionUserName();
      if (receiver) formData.append("receiver", receiver);
      if (personName.trim()) formData.append("sender", personName.trim());
      formData.append("payment_method", "cash");
      const res = await apiClient.post(`/customers/pay/${id}`, formData);
      const paymentId = res.data && res.data.payment ? res.data.payment.id : null;
      const previousOwed = toApiNumber(customerInfo?.total_owed, 0);
      const remainingAfter = Math.round(Math.max(0, previousOwed - paidAmt) * 100) / 100;
      const q = new URLSearchParams({
        old: String(previousOwed),
        paid: String(paidAmt),
        remain: String(remainingAfter),
        name: customerInfo?.name || "",
      });
      window.location.href = paymentId
        ? `/customers/${id}/receipts/${paymentId}?${q.toString()}`
        : `/customers/${id}/receipts?${q.toString()}`;
    } catch (error) {
      message.error(error?.response?.data?.message || t("errorMessage", "Failed to submit form. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sidePanel = (
    <div style={cardStyle}>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("customer", "Customer")}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginTop: 3 }}>{customerInfo?.name || "—"}</div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("previousDebtLabel", "Previous Debt")}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: T.text, marginTop: 3 }}>
            {formatCurrency(customerInfo?.total_owed || 0, mainCode)}
          </div>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("paidAmountLabel", "Amount Paid")}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: T.green, marginTop: 3 }}>
            {formatCurrency(appliedMainTotal, mainCode)}
          </div>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("remainingDebtLabel", "Remaining Debt")}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.red, marginTop: 3 }}>
            {formatCurrency(Math.max(0, toApiNumber(customerInfo?.total_owed, 0) - appliedMainTotal), mainCode)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <FormPage
      breadcrumb={[t("sidebarSectionContacts", "Contacts"), t("customers", "Customers"), t("payDebt", "Pay Debt")]}
      title={t("payDebt", "Pay Debt")}
      subtitle={customerInfo?.name || t("recordPayment", "Record a payment")}
      onBack={() => navigate("/customers")}
      onSave={PostApi}
      saving={isSubmitting}
      sidePanel={sidePanel}
    >
      <Form layout="vertical">
        <FormCard title={t("amountPaid", "Amount Paid")} subtitle={t("recordPaymentDescription", "Record a payment against this customer's debt")}>
          {useDualCurrency ? (
            <>
              <div style={grid2}>
                <Form.Item label={`${t("amountPaid", "Amount Paid")} (${mainCode})`}>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={amount_main}
                    onChange={(e) => setAmount_main(e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Form.Item>
                <Form.Item label={`${t("amountPaid", "Amount Paid")} (${secCode})`}>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={amount_secondary}
                    onChange={(e) => setAmount_secondary(e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Form.Item>
              </div>
              {exchangeRates.length > 0 && (
                <Form.Item label={t("exchangeRate", "Exchange rate")}>
                  <Select
                    placeholder={t("selectRate", "Select rate")}
                    value={exchangeRateId}
                    onChange={setExchangeRateId}
                    style={{ ...inputStyle, height: undefined, width: "100%" }}
                    options={exchangeRates.map((r) => ({
                      value: r.id,
                      label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secCode}`,
                    }))}
                  />
                </Form.Item>
              )}
            </>
          ) : (
            <Form.Item label={t("amountPaid", "Amount Paid")}>
              <Input
                type="number"
                min={0}
                step="any"
                value={amount_paid}
                onChange={(e) => setAmount_paid(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </Form.Item>
          )}
          <Form.Item label={t("noteOptional", "Note (optional)")}>
            <Input
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder={t("paymentNotePlaceholder", "e.g. paid by driver, receipt no…")}
              style={inputStyle}
            />
          </Form.Item>
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default Payment;
